import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FaCalendarAlt,
  FaBed,
  FaUsers,
  FaPercent,
  FaCheckCircle,
  FaTools,
  FaTimesCircle,
  FaDollarSign,
  FaChartLine,
} from 'react-icons/fa';
import Datepicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { format } from 'date-fns';
import axios from 'axios';
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  LineChart,
  Line,
  AreaChart,
  Area,
} from 'recharts';

const BQ_BASE_URL = process.env.REACT_APP_BQ_BASE_URL;

const STATUS_SHADES = {
  Available: '#111827',
  Reserved: '#4b5563',
  Occupied: '#9ca3af',
  Maintenance: '#d1d5db',
};

const Inventory = () => {
  const navigate = useNavigate();
  const [selectedDates, setSelectedDates] = useState([null, null]);
  const [calendarVisible, setCalendarVisible] = useState(false);
  const calendarRef = useRef(null);
  const buttonRef = useRef(null);
  const [roomDetails, setRoomDetails] = useState(null);
  const [roomInfo, setRoomInfo] = useState(null);
  const [rooms, setRooms] = useState([]);
  const [roomsLoading, setRoomsLoading] = useState(true);
  const [roomsError, setRoomsError] = useState(null);
  const [roomTypesData, setRoomTypesData] = useState({
    roomtypes: [],
    all_amenities: [],
    all_roomservices: [],
  });
  const [occupancyData, setOccupancyData] = useState({
    average_occupancy_rate: 0,
    total_booked_days: 0,
    total_possible_room_days: 0,
  });
  const [error, setError] = useState(null);

  // ---------- Data fetching ----------
  useEffect(() => {
    const fetchRoomDetails = async () => {
      try {
        const response = await fetch(`${BQ_BASE_URL}/bq/api/roomdetails`);
        if (!response.ok) throw new Error('Failed to fetch room details.');
        const data = await response.json();
        setRoomDetails(data);
      } catch (err) {
        setError(err.message);
      }
    };
    fetchRoomDetails();
  }, []);

  useEffect(() => {
    const fetchRoomInfo = async () => {
      try {
        const response = await fetch(`${BQ_BASE_URL}/bq/api/rooms/search`);
        const data = await response.json();
        setRoomInfo(data.rooms);
      } catch (error) {
        console.error('Error fetching room details:', error);
      }
    };
    fetchRoomInfo();
  }, []);

  // Fetch individual room records, including room type, status, amenities and pricing.
  useEffect(() => {
    const fetchRooms = async () => {
      try {
        setRoomsLoading(true);
        setRoomsError(null);

        const response = await axios.get(`${BQ_BASE_URL}/bq/api/rooms/`);
        setRooms(Array.isArray(response.data) ? response.data : []);
      } catch (error) {
        console.error('Error fetching rooms:', error);
        setRoomsError(error.response?.data?.detail || error.message || 'Failed to fetch rooms.');
      } finally {
        setRoomsLoading(false);
      }
    };

    fetchRooms();
  }, []);

  useEffect(() => {
    const fetchRoomTypes = async () => {
      try {
        const response = await axios.get(`${BQ_BASE_URL}/bq/api/admin-roomtypes/`);
        setRoomTypesData(response.data);
      } catch (error) {
        console.error('Error fetching room types:', error);
      }
    };
    fetchRoomTypes();
  }, []);

  useEffect(() => {
    if (selectedDates[0]) {
      const fetchOccupancyData = async () => {
        try {
          let response;
          let occupancy = {};
          if (selectedDates[1]) {
            response = await axios.get(`${BQ_BASE_URL}/bq/api/occupancy/average_occupancy_rate`, {
              params: {
                start_date: format(selectedDates[0], 'yyyy-MM-dd'),
                end_date: format(selectedDates[1], 'yyyy-MM-dd'),
              },
            });
            occupancy = {
              average_occupancy_rate: response.data.average_occupancy_rate,
              total_booked_days: response.data.total_booked_days,
              total_possible_room_days: response.data.total_possible_room_days,
            };
          } else {
            response = await axios.get(`${BQ_BASE_URL}/bq/api/occupancy/occupancy_rate_for_date`, {
              params: { date: format(selectedDates[0], 'yyyy-MM-dd') },
            });
            occupancy = {
              average_occupancy_rate: response.data.occupancy_rate,
              total_booked_days: response.data.total_booked_rooms,
              total_possible_room_days: response.data.total_rooms,
            };
          }
          setOccupancyData(occupancy);
        } catch (error) {
          console.error('Error fetching occupancy data:', error);
        }
      };
      fetchOccupancyData();
    }
  }, [selectedDates]);

  useEffect(() => {
    const fetchTodayOccupancy = async () => {
      try {
        const today = format(new Date(), 'yyyy-MM-dd');
        const response = await axios.get(
          `${BQ_BASE_URL}/bq/api/occupancy/occupancy_rate_for_date`,
          { params: { date: today } }
        );
        setOccupancyData({
          average_occupancy_rate: response.data.occupancy_rate,
          total_booked_days: response.data.total_booked_rooms,
          total_possible_room_days: response.data.total_rooms,
        });
      } catch (error) {
        console.error("Error fetching today's occupancy data:", error);
      }
    };
    fetchTodayOccupancy();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        calendarVisible &&
        calendarRef.current &&
        !calendarRef.current.contains(event.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target)
      ) {
        setCalendarVisible(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [calendarVisible]);

  const handleDateChange = (dates) => {
    const [start, end] = dates;
    setSelectedDates([start, end]);
    if (start && end) setCalendarVisible(false);
  };

  const handleCalendarToggle = () => setCalendarVisible(!calendarVisible);

  // ---------- Derived stats ----------
  const totalRooms = roomDetails?.total_rooms || 0;
  const statusCounts = roomDetails?.room_status_counts || {};
  const getStatus = (key) => statusCounts[key]?.count ?? statusCounts[key] ?? 0;

  const availableCount = getStatus('Available');
  const reservedCount = getStatus('Reserved');
  const occupiedCount = getStatus('Occupied');
  const maintenanceCount = getStatus('Under maintenance') || getStatus('Under Maintenance') || 0;

  const formatCurrency = (value) =>
    `₹${Number(value || 0).toLocaleString('en-IN', {
      maximumFractionDigits: 2,
    })}`;

  const getRoomStatusClass = (status) => {
    const normalizedStatus = String(status || '').toLowerCase();

    if (normalizedStatus === 'available') {
      return 'bg-green-50 text-green-700 border-green-200';
    }
    if (normalizedStatus === 'reserved') {
      return 'bg-yellow-50 text-yellow-700 border-yellow-200';
    }
    if (normalizedStatus === 'occupied') {
      return 'bg-blue-50 text-blue-700 border-blue-200';
    }
    if (normalizedStatus.includes('maintenance')) {
      return 'bg-red-50 text-red-700 border-red-200';
    }
    return 'bg-gray-100 text-gray-700 border-gray-200';
  };

  const occupiedPct = totalRooms ? Math.round((occupiedCount / totalRooms) * 100) : 0;
  const availablePct = totalRooms ? Math.round((availableCount / totalRooms) * 100) : 0;

  // ---------- Chart data ----------
  const pieData = [
    { name: 'Available', value: availableCount },
    { name: 'Reserved', value: reservedCount },
    { name: 'Occupied', value: occupiedCount },
    { name: 'Maintenance', value: maintenanceCount },
  ].filter((d) => d.value > 0);

  const roomTypesList = Object.keys(roomInfo || {});
  const stackedBarData = roomTypesList.map((t) => ({
    roomType: t,
    Available: roomInfo[t]?.Available?.count || 0,
    Reserved: roomInfo[t]?.Reserved?.count || 0,
    Occupied: roomInfo[t]?.Occupied?.count || 0,
    Maintenance: roomInfo[t]?.['Under maintenance']?.count || 0,
  }));

  const priceData = roomTypesData.roomtypes.map((r) => ({
    name: r.roomtypename,
    price: r.baseprice || 0,
    active: r.is_active,
  }));

  const trendData = [
    { label: 'Booked Days', value: occupancyData.total_booked_days },
    { label: 'Possible Days', value: occupancyData.total_possible_room_days },
  ];

  const tooltipStyle = {
    borderRadius: 6,
    border: '1px solid #e5e7eb',
    background: '#ffffff',
    boxShadow: '0 4px 12px rgba(0,0,0,0.06)',
    fontSize: 12,
    color: '#111827',
  };

  return (
    <div className="bg-gray-100 p-6">
      {/* ---------- HEADER (unchanged) ---------- */}
      <header className="flex flex-col md:flex-row justify-between items-start md:items-center mb-3 p-3 bg-white">
        <h1 className="text-black text-xl font-bold mb-2 md:mb-0 md:ml-2">Room Inventory</h1>
        <div className="flex flex-wrap gap-2 md:space-x-6">
          <button
            onClick={() => navigate('/dashboard/mdm/room-management/overview')}
            className="btn text-black font-bold"
          >
            Overview
          </button>
          <button onClick={() => navigate('/dashboard/mdm/room-management/rooms')} className="btn">
            Room Management
          </button>
        </div>
      </header>

      {/* ---------- DATE PICKER ---------- */}
      <div className="flex items-center justify-end gap-2 mb-6 relative">
        <div className="flex items-center space-x-2 bg-white border border-gray-200 p-2 rounded-md">
          <span className="font-medium text-gray-600 text-sm">Dates:</span>
          <span className="text-gray-900 font-medium text-sm">
            {selectedDates[0]
              ? `${format(selectedDates[0], 'dd MMM, yyyy')} ${
                  selectedDates[1] ? `- ${format(selectedDates[1], 'dd MMM, yyyy')}` : ''
                }`
              : format(new Date(), 'EEE MMM dd yyyy')}
          </span>
        </div>
        <button
          ref={buttonRef}
          onClick={handleCalendarToggle}
          className="bg-black text-white p-2.5 rounded-md hover:bg-gray-800 transition-colors"
        >
          <FaCalendarAlt className="text-sm" />
        </button>

        {calendarVisible && (
          <div
            ref={calendarRef}
            className="absolute z-50 p-4 bg-white border border-gray-200 shadow-lg rounded-md mt-1 right-0 top-full"
            style={{ width: 'auto', maxWidth: '95vw' }}
          >
            <h3 className="text-sm font-semibold text-gray-700 mb-2">Select Dates</h3>
            <Datepicker
              selected={selectedDates[0]}
              onChange={handleDateChange}
              startDate={selectedDates[0]}
              endDate={selectedDates[1]}
              selectsRange
              shouldCloseOnSelect={false}
              inline
            />
          </div>
        )}
      </div>

      {/* ---------- KPI CARDS ---------- */}
      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3 mb-6">
        <KpiCard icon={<FaBed />} label="Total Rooms" value={totalRooms} />
        <KpiCard
          icon={<FaCheckCircle />}
          label="Available"
          value={availableCount}
          sub={`${availablePct}% of inventory`}
        />
        <KpiCard
          icon={<FaUsers />}
          label="Occupied"
          value={occupiedCount}
          sub={`${occupiedPct}% of inventory`}
        />
        <KpiCard icon={<FaTimesCircle />} label="Reserved" value={reservedCount} />
        <KpiCard icon={<FaTools />} label="Maintenance" value={maintenanceCount} />
        <KpiCard
          icon={<FaPercent />}
          label="Avg Occupancy"
          value={`${occupancyData.average_occupancy_rate}%`}
          sub={`${occupancyData.total_booked_days}/${occupancyData.total_possible_room_days} room-days`}
        />
      </div>

      {/* ---------- ROOM STATUS TABLE ---------- */}
      <div className="bg-white border border-gray-200 rounded-lg p-5 mb-6">
        <h3 className="text-sm font-semibold text-gray-900 mb-4 uppercase tracking-wide">
          Room Status Overview
        </h3>
        <div className="overflow-x-auto">
          {roomInfo && Object.keys(roomInfo).length > 0 ? (
            <table className="min-w-full table-auto">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="py-3 px-4 text-left text-gray-500 font-medium text-xs uppercase tracking-wider">
                    Room Type
                  </th>
                  <th className="py-3 px-4 text-center text-gray-500 font-medium text-xs uppercase tracking-wider">
                    Available
                  </th>
                  <th className="py-3 px-4 text-center text-gray-500 font-medium text-xs uppercase tracking-wider">
                    Reserved
                  </th>
                  <th className="py-3 px-4 text-center text-gray-500 font-medium text-xs uppercase tracking-wider">
                    Occupied
                  </th>
                  <th className="py-3 px-4 text-center text-gray-500 font-medium text-xs uppercase tracking-wider">
                    Maintenance
                  </th>
                </tr>
              </thead>
              <tbody>
                {Object.keys(roomInfo).map((roomType) => {
                  const s = roomInfo[roomType];
                  return (
                    <tr
                      key={roomType}
                      className="border-b border-gray-100 hover:bg-gray-50 transition-colors"
                    >
                      <td className="py-3 px-4 text-gray-900 font-medium text-sm">{roomType}</td>
                      <td className="py-3 px-4 text-center">
                        <NumBadge value={s.Available?.count || 0} />
                      </td>
                      <td className="py-3 px-4 text-center">
                        <NumBadge value={s.Reserved?.count || 0} />
                      </td>
                      <td className="py-3 px-4 text-center">
                        <NumBadge value={s.Occupied?.count || 0} />
                      </td>
                      <td className="py-3 px-4 text-center">
                        <NumBadge value={s['Under maintenance']?.count || 0} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : (
            <p className="text-gray-500 text-center p-6 text-sm">Loading room details...</p>
          )}
        </div>
      </div>

      {/* ---------- ROOM TYPES ---------- */}
      <div className="mb-6">
        <h3 className="text-sm font-semibold text-gray-900 mb-4 uppercase tracking-wide">
          Room Types
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
          {roomTypesData.roomtypes.length === 0 && (
            <p className="text-gray-500 col-span-full text-center p-6 text-sm">
              Loading room types...
            </p>
          )}
          {roomTypesData.roomtypes.map((rt) => (
            <div
              key={rt.roomtypeid}
              className="bg-white border border-gray-200 rounded-lg overflow-hidden hover:border-gray-400 transition-colors"
            >
              <div className="h-16 bg-gray-50 border-b border-gray-200 relative flex items-center px-4">
                <FaBed className="text-gray-700 text-xl" />
                <div className="absolute top-3 right-3">
                  <span
                    className={`text-[10px] font-semibold px-2 py-0.5 rounded uppercase tracking-wider ${
                      rt.is_active ? 'bg-black text-white' : 'bg-gray-200 text-gray-600'
                    }`}
                  >
                    {rt.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>
              <div className="p-4">
                <div className="flex items-start justify-between gap-3">
                  <h4 className="text-base font-semibold text-gray-900">{rt.roomtypename}</h4>
                  <span className="text-base font-bold text-gray-900 whitespace-nowrap">
                    ₹{rt.baseprice?.toLocaleString('en-IN')}
                  </span>
                </div>
                <p className="text-xs text-gray-500 mt-1 line-clamp-2">{rt.description}</p>

                <div className="flex items-center gap-4 flex-wrap mt-3 pb-3 border-b border-gray-100 text-xs text-gray-600">
                  <span className="flex items-center gap-1">
                    <FaUsers className="text-gray-400" /> Max {rt.max_occupancy}
                  </span>
                  <span className="flex items-center gap-1">
                    <FaDollarSign className="text-gray-400" /> ₹{rt.deposit_amount || 0}
                  </span>
                  <span className="text-gray-700 font-medium">
                    {rt.is_refundable ? 'Refundable' : 'Non-refundable'}
                  </span>
                </div>

                {rt.amenities?.length > 0 && (
                  <div className="mt-3">
                    <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5">
                      Amenities
                    </p>
                    <div className="flex flex-wrap gap-1">
                      {rt.amenities.map((a) => (
                        <span
                          key={a.id}
                          className="text-xs bg-gray-100 text-gray-700 px-2 py-0.5 rounded"
                        >
                          {a.name}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {rt.service_categories?.length > 0 && (
                  <div className="mt-3">
                    <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5">
                      Services
                    </p>
                    <div className="flex flex-wrap gap-1">
                      {rt.service_categories.map((s) => (
                        <span
                          key={s.id}
                          className={`text-xs px-2 py-0.5 rounded border ${
                            s.isrelatedtofood
                              ? 'bg-white text-gray-800 border-gray-300'
                              : 'bg-gray-50 text-gray-600 border-gray-200'
                          }`}
                        >
                          {s.name}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ---------- INDIVIDUAL ROOM DETAILS (GET /rooms/) ---------- */}
      <div className="bg-white border border-gray-200 rounded-lg p-5 mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4">
          <div>
            <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">
              Individual Room Details
            </h3>
            <p className="text-xs text-gray-500 mt-1">
              Room-wise status, room type, amenities and calculated pricing
            </p>
          </div>
          {!roomsLoading && (
            <span className="text-xs text-gray-500">
              {rooms.length} {rooms.length === 1 ? 'room' : 'rooms'}
            </span>
          )}
        </div>

        {roomsLoading ? (
          <p className="text-gray-500 text-center p-6 text-sm">Loading rooms...</p>
        ) : roomsError ? (
          <p className="text-red-600 text-center p-6 text-sm">{roomsError}</p>
        ) : rooms.length === 0 ? (
          <p className="text-gray-500 text-center p-6 text-sm">No room records found.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full table-auto">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="py-3 px-3 text-left text-gray-500 font-medium text-xs uppercase tracking-wider">
                    Room No.
                  </th>
                  <th className="py-3 px-3 text-left text-gray-500 font-medium text-xs uppercase tracking-wider">
                    Room Type
                  </th>
                  <th className="py-3 px-3 text-left text-gray-500 font-medium text-xs uppercase tracking-wider">
                    Status
                  </th>
                  <th className="py-3 px-3 text-left text-gray-500 font-medium text-xs uppercase tracking-wider">
                    Condition
                  </th>
                  <th className="py-3 px-3 text-left text-gray-500 font-medium text-xs uppercase tracking-wider">
                    Add-Ons
                  </th>
                  <th className="py-3 px-3 text-right text-gray-500 font-medium text-xs uppercase tracking-wider">
                    Base Price
                  </th>
                  <th className="py-3 px-3 text-right text-gray-500 font-medium text-xs uppercase tracking-wider">
                    Add-Ons Total
                  </th>
                  <th className="py-3 px-3 text-right text-gray-500 font-medium text-xs uppercase tracking-wider">
                    Final Price
                  </th>
                </tr>
              </thead>
              <tbody>
                {rooms.map((room) => {
                  const roomAmenities = Array.isArray(room.amenities) ? room.amenities : [];

                  return (
                    <tr
                      key={room.roomid}
                      className="border-b border-gray-100 hover:bg-gray-50 transition-colors align-top"
                    >
                      <td className="py-3 px-3 text-sm font-semibold text-gray-900 whitespace-nowrap">
                        {room.roomnumber ?? '-'}
                      </td>
                      <td className="py-3 px-3 text-sm text-gray-700 whitespace-nowrap">
                        {room.roomtype?.roomtypename || '-'}
                      </td>
                      <td className="py-3 px-3">
                        <span
                          className={`inline-block px-2 py-1 rounded border text-xs font-medium whitespace-nowrap ${getRoomStatusClass(
                            room.status
                          )}`}
                        >
                          {room.status || '-'}
                        </span>
                      </td>
                      <td className="py-3 px-3 text-sm text-gray-600 whitespace-nowrap">
                        {String(room.condition || '-')
                          .replaceAll('_', ' ')
                          .replace(/^./, (character) => character.toUpperCase())}
                      </td>
                      <td className="py-3 px-3 text-xs text-gray-600 min-w-[180px]">
                        {roomAmenities.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {roomAmenities.map((amenity, index) => (
                              <span
                                key={`${room.roomid}-${amenity.factorid ?? index}`}
                                className="inline-block bg-gray-100 text-gray-700 px-2 py-1 rounded"
                                title={`${amenity.value_type || 'flat'}: ${amenity.value ?? 0}`}
                              >
                                {amenity.name}
                              </span>
                            ))}
                          </div>
                        ) : (
                          '-'
                        )}
                      </td>
                      <td className="py-3 px-3 text-sm text-gray-700 text-right whitespace-nowrap">
                        {formatCurrency(room.base_price ?? room.roomtype?.baseprice)}
                      </td>
                      <td className="py-3 px-3 text-sm text-gray-700 text-right whitespace-nowrap">
                        {formatCurrency(room.amenities_total)}
                      </td>
                      <td className="py-3 px-3 text-sm font-semibold text-gray-900 text-right whitespace-nowrap">
                        {formatCurrency(room.final_price)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ---------- CHARTS ROW 1 ---------- */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-6">
        {/* Donut */}
        <div className="bg-white border border-gray-200 rounded-lg p-5 lg:col-span-1">
          <h3 className="text-sm font-semibold text-gray-900 mb-4 uppercase tracking-wide">
            Room Status Split
          </h3>
          <div className="h-64 relative">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={pieData}
                  dataKey="value"
                  nameKey="name"
                  innerRadius={70}
                  outerRadius={100}
                  paddingAngle={2}
                  stroke="#fff"
                  strokeWidth={2}
                >
                  {pieData.map((entry) => (
                    <Cell key={entry.name} fill={STATUS_SHADES[entry.name] || '#9ca3af'} />
                  ))}
                </Pie>
                <Tooltip contentStyle={tooltipStyle} />
                <Legend
                  iconType="square"
                  wrapperStyle={{ fontSize: 11, color: '#4b5563' }}
                  verticalAlign="bottom"
                />
              </PieChart>
            </ResponsiveContainer>
            <div className="absolute inset-0 top-[-40px] flex items-center justify-center pointer-events-none">
              <div className="text-center">
                <p className="text-2xl font-bold text-gray-900">{totalRooms}</p>
                <p className="text-[10px] uppercase tracking-wider text-gray-500 font-medium">
                  Total
                </p>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2 mt-3">
            <StatRow label="Available" value={availableCount} />
            <StatRow label="Reserved" value={reservedCount} />
            <StatRow label="Occupied" value={occupiedCount} />
            <StatRow label="Maintenance" value={maintenanceCount} />
          </div>
        </div>

        {/* Occupancy performance */}
        <div className="bg-white border border-gray-200 rounded-lg p-5 lg:col-span-2">
          <h3 className="text-sm font-semibold text-gray-900 mb-4 uppercase tracking-wide">
            Occupancy Performance
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={trendData} margin={{ top: 10, right: 10, left: -18, bottom: 0 }}>
                  <defs>
                    <linearGradient id="occupancyFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#111827" stopOpacity={0.25} />
                      <stop offset="100%" stopColor="#111827" stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                  <XAxis
                    dataKey="label"
                    fontSize={11}
                    stroke="#9ca3af"
                    axisLine={{ stroke: '#e5e7eb' }}
                    tickLine={false}
                  />
                  <YAxis
                    fontSize={11}
                    stroke="#9ca3af"
                    axisLine={{ stroke: '#e5e7eb' }}
                    tickLine={false}
                  />
                  <Tooltip contentStyle={tooltipStyle} />
                  <Area
                    type="monotone"
                    dataKey="value"
                    stroke="#111827"
                    strokeWidth={2}
                    fill="url(#occupancyFill)"
                    dot={{ r: 4, fill: '#111827', strokeWidth: 2, stroke: '#fff' }}
                    activeDot={{ r: 6 }}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
            <div className="flex flex-col justify-center gap-4">
              <div className="border border-gray-200 rounded-md p-4">
                <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">
                  Average Occupancy Rate
                </p>
                <p className="text-4xl font-bold text-gray-900 mt-1">
                  {occupancyData.average_occupancy_rate}%
                </p>
                <div className="mt-3 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-black rounded-full transition-all duration-700"
                    style={{
                      width: `${Math.min(occupancyData.average_occupancy_rate, 100)}%`,
                    }}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <MiniStat
                  label="Booked Days"
                  value={occupancyData.total_booked_days}
                  icon={<FaChartLine />}
                />
                <MiniStat
                  label="Possible Days"
                  value={occupancyData.total_possible_room_days}
                  icon={<FaBed />}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ---------- CHARTS ROW 2 ---------- */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-6">
        {/* Stacked bar */}
        <div className="bg-white border border-gray-200 rounded-lg p-5">
          <h3 className="text-sm font-semibold text-gray-900 mb-4 uppercase tracking-wide">
            Status by Room Type
          </h3>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={stackedBarData} margin={{ top: 10, right: 10, left: -18, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                <XAxis
                  dataKey="roomType"
                  fontSize={10}
                  stroke="#9ca3af"
                  axisLine={{ stroke: '#e5e7eb' }}
                  tickLine={false}
                  interval={0}
                  angle={-12}
                  textAnchor="end"
                  height={60}
                />
                <YAxis
                  fontSize={11}
                  stroke="#9ca3af"
                  axisLine={{ stroke: '#e5e7eb' }}
                  tickLine={false}
                />
                <Tooltip contentStyle={tooltipStyle} />
                <Legend iconType="square" wrapperStyle={{ fontSize: 11, color: '#4b5563' }} />
                <Bar dataKey="Available" stackId="a" fill={STATUS_SHADES.Available} />
                <Bar dataKey="Reserved" stackId="a" fill={STATUS_SHADES.Reserved} />
                <Bar dataKey="Occupied" stackId="a" fill={STATUS_SHADES.Occupied} />
                <Bar dataKey="Maintenance" stackId="a" fill={STATUS_SHADES.Maintenance} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Price bar */}
        <div className="bg-white border border-gray-200 rounded-lg p-5">
          <h3 className="text-sm font-semibold text-gray-900 mb-4 uppercase tracking-wide">
            Base Price by Room Type
          </h3>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={priceData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                <XAxis
                  dataKey="name"
                  fontSize={10}
                  stroke="#9ca3af"
                  axisLine={{ stroke: '#e5e7eb' }}
                  tickLine={false}
                  interval={0}
                  angle={-12}
                  textAnchor="end"
                  height={60}
                />
                <YAxis
                  fontSize={11}
                  stroke="#9ca3af"
                  axisLine={{ stroke: '#e5e7eb' }}
                  tickLine={false}
                  tickFormatter={(v) => `₹${v}`}
                />
                <Tooltip
                  formatter={(v) => [`₹${v.toLocaleString('en-IN')}`, 'Base Price']}
                  contentStyle={tooltipStyle}
                  cursor={{ fill: '#f9fafb' }}
                />
                <Bar dataKey="price" fill="#111827" radius={[2, 2, 0, 0]} barSize={36} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ---------- Small helper components ---------- */

const KpiCard = ({ icon, label, value, sub }) => (
  <div className="bg-white border border-gray-200 rounded-lg p-4 hover:border-gray-400 transition-colors">
    <div className="flex items-center gap-2 text-gray-500 mb-2">
      <span className="text-sm">{icon}</span>
      <span className="text-xs font-medium uppercase tracking-wider">{label}</span>
    </div>
    <p className="text-2xl font-bold text-gray-900">{value}</p>
    {sub && <p className="text-[11px] text-gray-500 mt-0.5">{sub}</p>}
  </div>
);

const StatRow = ({ label, value }) => (
  <div className="flex items-center justify-between bg-white border border-gray-100 rounded px-3 py-2">
    <span className="text-xs text-gray-600 font-medium">{label}</span>
    <span className="text-sm font-semibold text-gray-900">{value}</span>
  </div>
);

const MiniStat = ({ label, value, icon }) => (
  <div className="bg-white border border-gray-200 rounded-md p-3">
    <div className="flex items-center gap-1.5 text-gray-500 text-xs font-medium uppercase tracking-wide">
      {icon} {label}
    </div>
    <p className="text-lg font-bold text-gray-900 mt-1">{value}</p>
  </div>
);

const NumBadge = ({ value }) => (
  <span className="inline-block min-w-[30px] px-2 py-0.5 rounded text-sm font-semibold text-gray-900 border border-gray-200 bg-white">
    {value}
  </span>
);

export default Inventory;

// import React, { useState, useEffect, useRef } from 'react';
// import { useNavigate } from 'react-router-dom';
// import { FaCalendarAlt } from 'react-icons/fa';
// import Datepicker from 'react-datepicker';
// import 'react-datepicker/dist/react-datepicker.css';
// import { format } from 'date-fns';
// import axios from 'axios';
// const BQ_BASE_URL = process.env.REACT_APP_BQ_BASE_URL;

// const Inventory = () => {
//   const navigate = useNavigate();
//   const [selectedDates, setSelectedDates] = useState([null, null]);
//   const [calendarVisible, setCalendarVisible] = useState(false);
//   const calendarRef = useRef(null);
//   const buttonRef = useRef(null);
//   const [roomDetails, setRoomDetails] = useState(null);
//   const [roomInfo, setRoomInfo] = useState(null);
//   const [occupancyData, setOccupancyData] = useState({
//     average_occupancy_rate: 0,
//     total_booked_days: 0,
//     total_possible_room_days: 0,
//   });
//   const [error, setError] = useState(null);

//   useEffect(() => {
//     const fetchRoomDetails = async () => {
//       try {
//         const response = await fetch(`${BQ_BASE_URL}/bq/api/roomdetails`);
//         if (!response.ok) {
//           throw new Error('Failed to fetch room details.');
//         }
//         const data = await response.json();
//         setRoomDetails(data);
//       } catch (err) {
//         setError(err.message);
//       }
//     };

//     fetchRoomDetails();
//   }, []);

//   useEffect(() => {
//     const fetchRoomInfo = async () => {
//       try {
//         const response = await fetch(`${BQ_BASE_URL}/bq/api/rooms/search`);
//         const data = await response.json();
//         setRoomInfo(data.rooms);
//       } catch (error) {
//         console.error('Error fetching room details:', error);
//       }
//     };

//     fetchRoomInfo();
//   }, []);

//   // Fetch occupancy data when selected dates change
//   useEffect(() => {
//     if (selectedDates[0]) {
//       const fetchOccupancyData = async () => {
//         try {
//           let response;
//           let occupancyData = {};
//           if (selectedDates[1]) {
//             // Date Range selected
//             response = await axios.get(`${BQ_BASE_URL}/bq/api/occupancy/average_occupancy_rate`, {
//               params: {
//                 start_date: format(selectedDates[0], 'yyyy-MM-dd'),
//                 end_date: format(selectedDates[1], 'yyyy-MM-dd'),
//               },
//             });
//             occupancyData = {
//               average_occupancy_rate: response.data.average_occupancy_rate,
//               total_booked_days: response.data.total_booked_days,
//               total_possible_room_days: response.data.total_possible_room_days,
//             };
//           } else {
//             // Single Date selected
//             response = await axios.get(`${BQ_BASE_URL}/bq/api/occupancy/occupancy_rate_for_date`, {
//               params: {
//                 date: format(selectedDates[0], 'yyyy-MM-dd'),
//               },
//             });
//             occupancyData = {
//               average_occupancy_rate: response.data.occupancy_rate,
//               total_booked_days: response.data.total_booked_rooms,
//               total_possible_room_days: response.data.total_rooms,
//             };
//           }
//           setOccupancyData(occupancyData);
//         } catch (error) {
//           console.error('Error fetching occupancy data:', error);
//         }
//       };

//       fetchOccupancyData();
//     }
//   }, [selectedDates]);

//   useEffect(() => {
//     const fetchTodayOccupancy = async () => {
//       try {
//         const today = format(new Date(), 'yyyy-MM-dd');
//         const response = await axios.get(
//           `${BQ_BASE_URL}/bq/api/occupancy/occupancy_rate_for_date`,
//           {
//             params: { date: today },
//           }
//         );
//         setOccupancyData({
//           average_occupancy_rate: response.data.occupancy_rate,
//           total_booked_days: response.data.total_booked_rooms,
//           total_possible_room_days: response.data.total_rooms,
//         });
//       } catch (error) {
//         console.error("Error fetching today's occupancy data:", error);
//       }
//     };

//     fetchTodayOccupancy();
//   }, []);

//   // Close calendar when clicking outside
//   useEffect(() => {
//     const handleClickOutside = (event) => {
//       if (
//         calendarVisible &&
//         calendarRef.current &&
//         !calendarRef.current.contains(event.target) &&
//         buttonRef.current &&
//         !buttonRef.current.contains(event.target)
//       ) {
//         setCalendarVisible(false);
//       }
//     };

//     document.addEventListener('mousedown', handleClickOutside);
//     return () => {
//       document.removeEventListener('mousedown', handleClickOutside);
//     };
//   }, [calendarVisible]);

//   const handleDateChange = (dates) => {
//     const [start, end] = dates;
//     setSelectedDates([start, end]);

//     // Close calendar when both dates are selected in range mode
//     if (start && end) {
//       setCalendarVisible(false);
//     }
//   };

//   const handleCalendarToggle = () => {
//     setCalendarVisible(!calendarVisible);
//   };

//   return (
//     <div className="bg-gray-100 p-6 relative min-h-screen">
//       <header className="flex flex-col md:flex-row justify-between items-start md:items-center mb-3 bg-white p-3">
//         <h1 className="text-black text-xl font-bold mb-2 md:mb-0 md:ml-2">Room Inventory</h1>
//         <div className="flex flex-wrap gap-2 md:space-x-6">
//           <button
//             onClick={() => navigate('/dashboard/mdm/room-management/overview')}
//             className="btn text-black font-bold"
//           >
//             Overview
//           </button>
//           <button onClick={() => navigate('/dashboard/mdm/room-management/rooms')} className="btn">
//             Room Management
//           </button>
//           {/* <button
//             onClick={() =>
//               navigate("/dashboard/mdm/room-management/order-summary")
//             }
//             className="btn"
//           >
//             Order Summary
//           </button> */}
//         </div>
//       </header>

//       {/* Selected Dates and Calendar */}
//       <div className="mb-6 flex flex-col sm:flex-row items-start sm:items-center justify-end relative">
//         <div className="flex items-center space-x-2 bg-white p-2 mb-2 sm:mb-0">
//           <span className="font-semibold text-black">Select Dates:</span>
//           <span className="border border-gray-300 px-3 py-1 inline-block">
//             <span className="text-gray-900 font-semibold">
//               {selectedDates[0]
//                 ? `${format(selectedDates[0], 'dd MMM, yyyy')} ${
//                     selectedDates[1] ? `- ${format(selectedDates[1], 'dd MMM, yyyy')}` : ''
//                   }`
//                 : format(new Date(), 'EEE MMM dd yyyy')}
//             </span>
//           </span>
//         </div>
//         <button
//           ref={buttonRef}
//           onClick={handleCalendarToggle}
//           className="btn flex items-center bg-white p-3.5"
//         >
//           <FaCalendarAlt className="text-xl" />
//         </button>

//         {calendarVisible && (
//           <div
//             ref={calendarRef}
//             className="absolute z-50 p-4 bg-white shadow-lg rounded-md mt-1 right-0 top-full"
//             style={{
//               width: 'auto',
//               maxWidth: '95vw',
//             }}
//           >
//             <h3 className="text-base font-semibold text-gray-700 mb-2">Select Dates</h3>
//             <Datepicker
//               selected={selectedDates[0]}
//               onChange={handleDateChange}
//               startDate={selectedDates[0]}
//               endDate={selectedDates[1]}
//               selectsRange
//               shouldCloseOnSelect={false}
//               inline
//             />
//           </div>
//         )}
//       </div>

//       {/* Key Statistics */}
//       <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 mb-4">
//         <div className="bg-white p-3 rounded-lg shadow-md">
//           <h3 className="font-semibold text-sm text-gray-700">Total Rooms</h3>
//           <p className="text-3xl font-bold text-gray-800">{roomDetails?.total_rooms || 0}</p>
//         </div>
//         {roomDetails?.room_status_counts
//           ? Object.keys(roomDetails.room_status_counts).map((type) => {
//               const counts = roomDetails.room_status_counts[type] || 0;
//               return (
//                 <div className="bg-white p-3 rounded-lg shadow-md" key={type}>
//                   <h3 className="font-semibold text-sm whitespace-nowrap text-gray-700">{type}</h3>
//                   <p className="text-3xl font-bold text-gray-800">{counts}</p>
//                 </div>
//               );
//             })
//           : null}

//         {/* New Occupancy Data Cards */}
//         <div className="bg-white p-3 rounded-lg shadow-md">
//           <h3 className="font-semibold text-sm whitespace-nowrap text-gray-700">
//             Average Occupancy
//           </h3>
//           <p className="text-3xl font-bold text-gray-800">
//             {occupancyData.average_occupancy_rate}%
//           </p>
//         </div>
//       </div>

//       {/* Room Status Overview */}
//       <div className="mb-2">
//         <h2 className="text-xl font-semibold text-gray-800 mb-4">Room Status Overview</h2>
//         <div className="overflow-x-auto bg-white rounded-lg shadow-md">
//           {roomInfo && Object.keys(roomInfo).length > 0 ? (
//             <table className="min-w-full table-auto border-collapse">
//               <thead>
//                 <tr className="bg-gray-200">
//                   <th className="py-3 px-4 text-left text-gray-700 font-semibold">Room Type</th>
//                   <th className="py-3 px-4 text-center text-gray-700 font-semibold">
//                     Available Rooms
//                   </th>
//                   <th className="py-3 px-4 text-center text-gray-700 font-semibold">
//                     Reserved Rooms
//                   </th>
//                   <th className="py-3 px-4 text-center text-gray-700 font-semibold">
//                     Occupied Rooms
//                   </th>
//                   <th className="py-3 px-4 text-center text-gray-700 font-semibold">
//                     Under Maintenance
//                   </th>
//                 </tr>
//               </thead>
//               <tbody>
//                 {Object.keys(roomInfo).map((roomType) => {
//                   const statusCounts = roomInfo[roomType];
//                   return (
//                     <tr key={roomType} className="hover:bg-gray-50 border-b">
//                       <td className="py-3 px-4 text-gray-700">{roomType}</td>
//                       <td className="py-3 px-4 text-center text-green-600 font-medium">
//                         {statusCounts.Available ? statusCounts.Available.count : 0}
//                       </td>
//                       <td className="py-3 px-4 text-center text-red-600 font-medium">
//                         {statusCounts.Reserved ? statusCounts.Reserved.count : 0}
//                       </td>
//                       <td className="py-3 px-4 text-center text-cyan-600 font-medium">
//                         {statusCounts.Occupied ? statusCounts.Occupied.count : 0}
//                       </td>
//                       <td className="py-3 px-4 text-center text-cyan-600 font-medium">
//                         {statusCounts['Under maintenance']
//                           ? statusCounts['Under maintenance'].count
//                           : 0}
//                       </td>
//                     </tr>
//                   );
//                 })}
//               </tbody>
//             </table>
//           ) : (
//             <p className="text-gray-600 text-center p-4">Room details loading...</p>
//           )}
//         </div>
//       </div>
//     </div>
//   );
// };

// export default Inventory;
